/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.userAmazonCatalogue

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.userAmazonCatalogue.internal.UserAmazonCatalogueUseCase
import features.userAmazonCatalogue.internal.UserAmazonCatalogueUseCase.Params
import features.userAmazonCatalogue.models.UserRewardsModel


typealias UserAmazonCatalogueState = CFlow<UserAmazonCatalogueUseCaseResult?>

typealias UserAmazonCatalogueUseCaseResult = Result<UserRewardsModel>

fun UserAmazonCatalogueUseCaseResult.serializeUserAmazonCatalogueApiResult(): SerializedResult<String> {
    return this.toJsonResult<UserRewardsModel>()
}

/**
 * Serializes [UserAmazonCatalogueState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun UserAmazonCatalogueState.serializeUserAmazonCatalogueState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<UserRewardsModel>()
    }.asCFlow()
}

/**
 * Get a list of rewards available to this user including the amazon catalogue. [Api] class
 */
class UserAmazonCatalogueApi : Api() {

    val userAmazonCatalogueState: UserAmazonCatalogueState
        get() = _userAmazonCatalogueState.asCFlow()

    companion object {
        private val _userAmazonCatalogueState: MutableStateFlow<UserAmazonCatalogueUseCaseResult> = MutableStateFlow(Idle())
    }

    private val userAmazonCatalogueUseCase = UserAmazonCatalogueUseCase()

    /**
     * Get a list of rewards available to this user including the amazon catalogue.
     */
    suspend fun getUserAmazonCatalogue(
        overrideHeaders: Map<String, String>? = null,
        pageNumber: Int,
        perPage: Int,
        keywords: String?,
        filters: String?
    ): Result<UserRewardsModel> {
        /* set state to Loading as soon as function is called */
        _userAmazonCatalogueState.value = Loading()
        val result = this.userAmazonCatalogueUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                pageNumber = pageNumber,
                perPage = perPage,
                keywords = keywords,
                filters = filters
            )
        ) { result ->
            _userAmazonCatalogueState.value = result
            return@userAmazonCatalogueUseCase result
        }
        return result
    }

}
